import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';
import type { OrganizationWithRelations, OrganizationFormData } from '@/types/organization.types';
import { Sentry } from "@/lib/sentry";

export interface OrganizationFilters {
  search?: string;
  status?: string;
  tier?: string;
  industry?: string;
  minRevenue?: number;
  maxRevenue?: number;
  hasContacts?: boolean;
  hasOpportunities?: boolean;
}

export interface PaginatedOrganizationsParams {
  offset: number;
  limit: number;
  filters?: OrganizationFilters;
  sortBy?: 'name' | 'created_at' | 'annual_revenue' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export const fetchOrganizations = async () => {
  Sentry.addBreadcrumb({
    message: 'Fetching organizations',
    category: 'database',
    level: 'info',
  });

  try {
    const { data, error } = await supabase.from('organizations').select('*').order('name');

    if (error) {
      Sentry.captureException(error, {
        tags: { operation: 'fetchOrganizations' },
        extra: { message: 'Failed to fetch organizations', error: error.message, code: error.code },
      });
      logError(`Failed to fetch organizations: ${error.message}`, error);
      
      // If it's an auth error, provide a more helpful message
      if (error.code === 'PGRST116' || error.message.includes('JWT')) {
        console.warn('Authentication required to access organizations');
        return [];
      }
      
      throw error;
    }

    console.log('Organizations fetched successfully:', data?.length || 0);
    return data || [];
  } catch (err) {
    console.error('Critical error in fetchOrganizations:', err);
    throw err;
  }
};

export const fetchOrganizationById = async (id: string) => {
  const { data, error } = await supabase
    .from('organizations')
    .select(
      `
      *,
      contacts:contacts!contacts_organization_id_fkey(id, first_name, last_name, email, job_title),
      parent:parent_id(id, name)
    `
    )
    .eq('id', id)
    .maybeSingle();

  if (error) {
    logError('Error fetching organization by ID:', error);
    throw error;
  }
  return data;
};

export const createOrganization = async (organizationData: OrganizationFormData) => {
  const { data, error } = await supabase
    .from('organizations')
    .insert(organizationData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateOrganization = async (
  id: string,
  organizationData: Partial<OrganizationFormData>
) => {
  const { data, error } = await supabase
    .from('organizations')
    .update(organizationData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteOrganization = async (id: string) => {
  // First check if there are any child organizations
  const { data: children, error: childrenError } = await supabase
    .from('organizations')
    .select('id')
    .eq('parent_id', id);

  if (childrenError) throw childrenError;

  if (children && children.length > 0) {
    throw new Error(
      'Cannot delete an organization with child organizations. Please move or delete the child organizations first.'
    );
  }

  // Check for related contacts
  const { data: contacts, error: contactsError } = await supabase
    .from('contacts')
    .select('id')
    .eq('organization_id', id);

  if (contactsError) throw contactsError;

  // Check for related opportunities
  const { data: opportunities, error: opportunitiesError } = await supabase
    .from('opportunities')
    .select('id')
    .eq('organization_id', id);

  if (opportunitiesError) throw opportunitiesError;

  // Set up error message if there are dependencies
  const dependencies = [];
  if (contacts && contacts.length > 0) dependencies.push(`${contacts.length} contacts`);
  if (opportunities && opportunities.length > 0)
    dependencies.push(`${opportunities.length} opportunities`);

  if (dependencies.length > 0) {
    throw new Error(
      `Cannot delete an organization with related records: ${dependencies.join(', ')}. Please reassign or delete these records first.`
    );
  }

  // If we get here, we can delete the organization
  const { error } = await supabase.from('organizations').delete().eq('id', id);

  if (error) throw error;
  return true;
};

export const mergeOrganizations = async (sourceId: string, targetId: string) => {
  // Verify both organizations exist
  const { data: sourceOrg, error: sourceError } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', sourceId)
    .single();

  if (sourceError) throw sourceError;

  const { data: targetOrg, error: targetError } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', targetId)
    .single();

  if (targetError) throw targetError;

  if (!sourceOrg || !targetOrg) {
    throw new Error('Both source and target organizations must exist');
  }

  // Start a transaction for the merge
  const { error: transactionError } = await supabase.rpc('merge_organizations', {
    source_id: sourceId,
    target_id: targetId,
  });

  if (transactionError) throw transactionError;

  return { success: true, message: `Successfully merged ${sourceOrg.name} into ${targetOrg.name}` };
};

export const fetchOrganizationHierarchy = async (id: string) => {
  // Get the ancestors (parent organizations)
  const { data: ancestors, error: ancestorsError } = await supabase
    .from('organizations')
    .select('id, name, parent_id')
    .filter('hierarchy_path', 'cs', `{${id}}`)
    .order('hierarchy_level', { ascending: true });

  if (ancestorsError) throw ancestorsError;

  // Get the descendants (child organizations)
  const { data: descendants, error: descendantsError } = await supabase
    .from('organizations')
    .select('id, name, parent_id, hierarchy_level')
    .filter('hierarchy_path', 'cs', `{${id}}`)
    .neq('id', id)
    .order('hierarchy_level', { ascending: true });

  if (descendantsError) throw descendantsError;

  return { ancestors, descendants };
};

export const fetchOrganizationChildren = async (parentId: string) => {
  const { data, error } = await supabase
    .from('organizations')
    .select('id, name, parent_id, tier, status')
    .eq('parent_id', parentId)
    .order('name');

  if (error) throw error;
  return data;
};

export const fetchRootOrganizations = async () => {
  const { data, error } = await supabase
    .from('organizations')
    .select('id, name, tier, status')
    .is('parent_id', null)
    .order('name');

  if (error) throw error;
  return data;
};

/**
 * Enhanced organization service functions for better performance
 */

export const fetchOrganizationsPaginated = async ({
  offset,
  limit,
  filters = {},
  sortBy = 'name',
  sortOrder = 'asc',
}: PaginatedOrganizationsParams): Promise<OrganizationWithRelations[]> => {
  try {
    let query = supabase
      .from('organizations')
      .select(
        `
        *,
        contacts:contacts!contacts_organization_id_fkey(id, first_name, last_name, email, is_primary),
        opportunities:opportunities!opportunities_organization_id_fkey(id, name, value, stage, probability)
      `
      )
      .range(offset, offset + limit - 1)
      .order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply filters
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,industry.ilike.%${filters.search}%`);
    }

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.tier && filters.tier !== 'all') {
      query = query.eq('tier', filters.tier);
    }

    if (filters.industry && filters.industry !== 'all') {
      query = query.eq('industry', filters.industry);
    }

    if (filters.minRevenue !== undefined) {
      query = query.gte('annual_revenue', filters.minRevenue);
    }

    if (filters.maxRevenue !== undefined) {
      query = query.lte('annual_revenue', filters.maxRevenue);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Post-process data for additional filters
    let filteredData = data || [];

    if (filters.hasContacts) {
      filteredData = filteredData.filter((org) => org.contacts && org.contacts.length > 0);
    }

    if (filters.hasOpportunities) {
      filteredData = filteredData.filter(
        (org) => org.opportunities && org.opportunities.length > 0
      );
    }

    return filteredData.map((org) => ({
      ...org,
      contacts: org.contacts || [],
      opportunities: org.opportunities || [],
    }));
  } catch (error: any) {
    logError('Error fetching paginated organizations:', error);
    throw new Error(`Failed to fetch organizations: ${error.message}`);
  }
};

export const fetchOrganizationWithDetails = async (
  id: string
): Promise<OrganizationWithRelations> => {
  try {
    // Single optimized query with all related data
    const { data, error } = await supabase
      .from('organizations')
      .select(
        `
        *,
        contacts:contacts!contacts_organization_id_fkey(
          id, first_name, last_name, email, job_title, phone, 
          organization_id, influence_level, is_primary, department, status
        ),
        opportunities:opportunities!opportunities_organization_id_fkey(
          id, name, stage, value, probability, expected_close_date, created_at
        ),
        activities:activities!activities_organization_id_fkey(
          id, title, type, date, description, created_at
        ),
        organization_services:organization_services!organization_services_organization_id_fkey(
          id, status, start_date, renewal_date,
          services(id, name, category, description)
        ),
        projects:projects!projects_organization_id_fkey(
          id, name, status, budget, start_date, end_date
        ),
        parent:parent_id(id, name)
      `
      )
      .eq('id', id)
      .maybeSingle();

    if (error) {
      logError('Error fetching organization details:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Organization not found');
    }

    // Fetch children separately due to PostgREST limitations with self-referential relationships
    const { data: children } = await supabase
      .from('organizations')
      .select('id, name, status, tier')
      .eq('parent_id', id);

    // Format services data
    const formattedServices = (data.organization_services || []).map((item) => {
      const service = item.services as any;
      return {
        id: item.id,
        name: service?.name || 'Unknown Service',
        category: service?.category || 'General',
        description: service?.description,
        status: item.status || 'active',
        startDate: item.start_date,
        renewalDate: item.renewal_date,
      };
    });

    return {
      ...data,
      size: data.employee_count ? data.employee_count.toString() : undefined, // Map employee_count to size
      contacts: data.contacts || [],
      opportunities: data.opportunities || [],
      activities: data.activities || [],
      services: formattedServices,
      projects: data.projects || [],
      parent: data.parent,
      children: children || [],
    };
  } catch (error: any) {
    logError('Error fetching organization details:', error);
    throw new Error(`Failed to fetch organization: ${error.message}`);
  }
};
